/* eslint-disable i18next/no-literal-string */
import { logger } from 'monitor';
import { type ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  formControlClasses,
  type TextFieldProps as MuiTextFieldProps,
  outlinedInputClasses,
  Stack,
  TextField,
} from 'ui';

import { type CountryType, countries } from '../CountryAutocompleteField/countries';

export type PhoneNumberFieldProps = MuiTextFieldProps & {
  name: string;
  withFlags?: boolean;
  'data-testid'?: string;
};

const E164_REGEX = /^\+[1-9]\d{1,14}$/; // telephone number standard https://en.wikipedia.org/wiki/E.164
const INVALID_PHONE_MESSAGE = 'Invalid phone number. Use international format, e.g. +447806574241';

const normalizeCountryCode = (code: string) => code.replace(/[^\d]/g, '');

// Pre-process countries with lowercase versions for efficient searching
type ProcessedCountryType = {
  country: CountryType;
  nameLower: string;
  abbrLower: string;
  codeWithPlus: string;
};

const processedCountries: ProcessedCountryType[] = countries.map((country) => ({
  abbrLower: country.abbr.toLowerCase(),
  codeWithPlus: `+${country.code}`,
  country,
  nameLower: country.name.toLowerCase(),
}));

const countriesByCode = new Map<string, CountryType[]>();

for (const country of countries) {
  const normalizedCode = normalizeCountryCode(country.code);
  const existing = countriesByCode.get(normalizedCode);
  if (existing) {
    existing.push(country);
  } else {
    countriesByCode.set(normalizedCode, [country]);
  }
}

const sortedCountryCodes = Array.from(countriesByCode.keys()).sort((a, b) => b.length - a.length);

const getDefaultCountry = () => {
  const gb = countries.find((country) => country.abbr === 'GB');
  if (gb) {
    return gb;
  }
  if (countries.length > 0) {
    logger.warn('Default country GB not found, falling back to first country');
    return countries[0];
  }
  throw new Error('Countries list is empty');
};

const DEFAULT_COUNTRY = getDefaultCountry();
const DEFAULT_NUMBER = '';

const sanitizeNationalNumber = (value: string) => value.replace(/[^\d]/g, '');
const getCountryGroup = (country: CountryType) => (country.suggested ? 'Suggested' : 'Other');
const sortSuggestedCountriesFirst = (options: CountryType[]) => {
  const suggestedOptions = options.filter((option) => option.suggested);
  const otherOptions = options.filter((option) => !option.suggested);

  return [...suggestedOptions, ...otherOptions];
};

const normalizeE164 = (value: string) => {
  const raw = value.trim();
  if (!raw) return null;
  const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
  const digits = withPlus.replace(/[^\d]/g, '');
  if (!digits) return null;
  const normalized = `+${digits}`;
  return E164_REGEX.test(normalized) ? normalized : null;
};

const extractCodeAndNumber = (value: string, preferredCountry?: CountryType) => {
  const normalized = normalizeE164(value);
  if (!normalized) {
    logger.warn('unable to parse phone number', { value });
    return null;
  }

  const matchedCode = sortedCountryCodes.find((code) => normalized.startsWith(`+${code}`));
  if (!matchedCode) {
    logger.warn('unable to find selected country code', { value: normalized });
    return null;
  }

  const matches = countriesByCode.get(matchedCode) ?? [];
  let country = matches[0];
  if (preferredCountry && matches.some((candidate) => candidate.abbr === preferredCountry.abbr)) {
    country = preferredCountry;
  }
  if (!country) {
    logger.warn('unable to resolve country from code', { code: matchedCode, value: normalized });
    return null;
  }

  return {
    country,
    phoneNumber: normalized.replace(`+${matchedCode}`, ''),
  };
};

const Field = (
  { required, name, label, withFlags = false, size, 'data-testid': dataTestId, ...rest }: PhoneNumberFieldProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const { control, clearErrors, setError } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? 'This field is required' : false,
      validate: (value) => {
        if (!value && !required) return true;
        const result = extractCodeAndNumber(String(value));
        if (!result || !result.phoneNumber) {
          return INVALID_PHONE_MESSAGE;
        }
        return true;
      },
    },
  });
  const { onChange } = field;

  const [country, setCountryState] = useState<CountryType>(DEFAULT_COUNTRY);
  const [phoneNumber, setPhoneNumberState] = useState<string>(DEFAULT_NUMBER);
  const [flagLoadFailed, setFlagLoadFailed] = useState(false);

  const countryRef = useRef<CountryType>(DEFAULT_COUNTRY);
  const phoneNumberRef = useRef<string>(DEFAULT_NUMBER);

  const setCountry = useCallback((next: CountryType) => {
    countryRef.current = next;
    setCountryState(next);
  }, []);

  const setPhoneNumber = useCallback((next: string) => {
    phoneNumberRef.current = next;
    setPhoneNumberState(next);
  }, []);

  const phoneInputAriaLabel = typeof label === 'string' ? label : 'Phone number';
  const orderedCountries = useMemo(() => sortSuggestedCountriesFirst(countries), []);

  const updateField = (code: string, number: string) => {
    onChange(`+${normalizeCountryCode(code)}${number}`);
  };

  const filterOptions = useCallback((options: CountryType[], { inputValue }: { inputValue: string }) => {
    if (!inputValue) return sortSuggestedCountriesFirst(options);

    const searchValue = inputValue.toLowerCase();
    const numericSearch = inputValue.replace(/[^\d]/g, '');
    const hasLetters = /[a-z]/i.test(searchValue);

    const filteredProcessed = processedCountries.filter((processedOption) => {
      if (hasLetters) {
        // Search by country name (pre-lowercased)
        if (processedOption.nameLower.includes(searchValue)) {
          return true;
        }
        // Search by country abbreviation (pre-lowercased)
        if (processedOption.abbrLower.includes(searchValue)) {
          return true;
        }
      }

      if (!numericSearch) {
        return false;
      }

      const normalizedCode = normalizeCountryCode(processedOption.country.code);
      // Prefer prefix matching for numeric input to avoid unrelated hits.
      return normalizedCode.startsWith(numericSearch);
    });

    return sortSuggestedCountriesFirst(filteredProcessed.map((processed) => processed.country));
  }, []);

  useEffect(() => {
    if (!field.value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFlagLoadFailed(false);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (countryRef.current.abbr !== DEFAULT_COUNTRY.abbr) {
        setCountry(DEFAULT_COUNTRY);
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (phoneNumberRef.current !== DEFAULT_NUMBER) {
        setPhoneNumber(DEFAULT_NUMBER);
      }
      return;
    }

    const current = extractCodeAndNumber(String(field.value), countryRef.current);
    if (!current) {
      setError(name, { message: INVALID_PHONE_MESSAGE, type: 'validate' });
      return;
    }
    clearErrors(name);

    if (current.country.abbr !== countryRef.current.abbr) {
      setCountry(current.country);
      setFlagLoadFailed(false);
    }
    if (current.phoneNumber !== phoneNumberRef.current) {
      setPhoneNumber(current.phoneNumber);
    }
  }, [clearErrors, field.value, name, setCountry, setError, setPhoneNumber]);

  return (
    <Stack
      direction="row"
      sx={(theme) => ({
        [`& .${formControlClasses.root}:nth-of-type(1)`]: {
          flex: `1 0 ${theme.spacing(19)}`,
        },
        gap: 0.5,
      })}
      data-testid={dataTestId}
    >
      <Autocomplete<CountryType>
        options={orderedCountries}
        value={country}
        disablePortal={false}
        disableClearable
        onBlurCapture={(event) => {
          const value = (event.target as HTMLInputElement).value;
          if (!value) {
            return;
          }
          const extractedCountry = extractCodeAndNumber(value.startsWith('+') ? value : `+${value}`, country);
          if (!extractedCountry) {
            setError(name, { message: INVALID_PHONE_MESSAGE, type: 'validate' });
            return;
          }
          clearErrors(name);

          if (extractedCountry.country.code === country.code) {
            return;
          }
          setCountry(extractedCountry.country);
          setFlagLoadFailed(false);
        }}
        onChange={(_event, newValue) => {
          if (!newValue) {
            logger.debug('no country selected');
            return;
          }
          clearErrors(name);
          setCountry(newValue);
          setFlagLoadFailed(false);
          updateField(newValue.code, phoneNumber);
        }}
        label="Country code"
        fullWidth={true}
        size={size}
        startAdornment={
          withFlags && country && !flagLoadFailed ? (
            <Box
              component="img"
              src={`https://flagcdn.com/40x30/${country.abbr.toLowerCase()}.webp`}
              alt={country.name}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setFlagLoadFailed(true)}
              sx={(theme) => ({
                borderRadius: theme.spacing(0.25),
                height: theme.spacing(1.875),
                ml: 1.5,
                objectFit: 'cover',
                width: theme.spacing(2.5),
              })}
            />
          ) : null
        }
        sx={{
          [`& .${outlinedInputClasses.root}`]: {
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
          },
        }}
        data-testid="country-code-autocomplete"
        getOptionLabel={(option) => `+${option.code}`}
        filterOptions={filterOptions}
        groupBy={getCountryGroup}
        renderOption={(props, option) => {
          const { key: _key, ...optionProps } = props;
          return (
            <Box
              component="li"
              {...optionProps}
              key={`${option.code}-${option.abbr}`}
              sx={(theme) => ({
                maxWidth: theme.spacing(23),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textWrap: 'nowrap',
              })}
            >
              {`+${option.code} ${option.name}`}
            </Box>
          );
        }}
      />
      <TextField
        size={size}
        value={phoneNumber}
        fullWidth
        onChange={(event) => {
          const sanitized = sanitizeNationalNumber(event.target.value);
          clearErrors(name);
          setPhoneNumber(sanitized);
          updateField(country.code, sanitized);
        }}
        label={label}
        sx={{
          [`& .${outlinedInputClasses.root}`]: {
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
          },
        }}
        slotProps={{
          htmlInput: {
            'aria-label': phoneInputAriaLabel,
            autoComplete: 'tel',
            inputMode: 'tel',
          },
        }}
        data-testid="phone-number-input"
        ref={ref}
        {...rest}
        error={Boolean(fieldState.error)}
        helperText={fieldState.error?.message}
      />
    </Stack>
  );
};
export const PhoneNumberField = forwardRef(Field);
